// this file contains version which is a string that is intended to be populated with version data from a combination of git tags and commit hash
// see README.md for local environemnt setup

import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { tap, switchMap, of, from, catchError } from 'rxjs';
import { UserService } from 'capiche-auth';
import { CapicheMaterialModule } from 'capiche-material';
import { StudentDisplayService, StudentDisplay } from '../../services/student-display.service';
import { CAPICHE_UI_CONFIG } from '../../capiche-ui.config';

@Component({
  selector: 'current-user-string',
  imports: [CommonModule, CapicheMaterialModule],
  templateUrl: './current-user.component.html',
  styleUrls: ['./current-user.component.css'],
})
export class CurrentUserComponent implements OnInit {
  public currentUserEmail: string = '';
  /**
   * CSW-1651: students have no email, so this footer showed nothing to
   * identify who's logged in. username/schoolName come from
   * StudentDisplayService's in-memory cache (set at login by
   * school-login.component.ts) — falling back to GET /auth/me only when
   * that cache is empty (a hard refresh loses in-memory state). Neither
   * field is a credential, and deliberately neither lives in Firebase
   * custom claims — see EducationService.authenticateStudent's doc
   * comment for why mixing display data into the access-control claims
   * object isn't worth it.
   */
  public studentDisplay: StudentDisplay | null = null;

  private readonly http = inject(HttpClient);
  private readonly studentDisplayService = inject(StudentDisplayService);
  private readonly config = inject(CAPICHE_UI_CONFIG);

  constructor(private userService: UserService) {}

  ngOnInit(): void {
    this.userService.currentUser$
      .pipe(
        tap((user) => {
          this.currentUserEmail = user?.email!;
        }),
        switchMap((user) => {
          if (!user || this.currentUserEmail) {
            this.studentDisplayService.clear();
            return of(null);
          }
          const cached = this.studentDisplayService.get();
          if (cached) return of(cached);
          if (!this.config.baseEducationApiUrl) return of(null);
          return from(user.user.getIdToken()).pipe(
            switchMap((token) =>
              this.http.get<StudentDisplay>(`${this.config.baseEducationApiUrl}/auth/me`, {
                headers: { Authorization: `Bearer ${token}` },
              }),
            ),
            catchError(() => of(null)),
          );
        }),
        tap((display) => {
          this.studentDisplay = display;
          if (display) this.studentDisplayService.set(display);
        }),
      )
      .subscribe();
  }
}
